/**
 * Tab Sync Coordinator - Orchestrates cross-tab synchronization
 * 
 * Responsibilities:
 * - Execute commands + broadcast to other tabs
 * - Process events + reload memory + broadcast to other tabs
 * - Listen to broadcasts from other tabs → reload memory
 * - Coordinate snapshot recovery
 * 
 * Architecture:
 * - Coordination layer (sits between UI and Domain)
 * - Owns ALL cross-tab synchronization logic
 * - Uses pure domain services (CommandExecutor, EventProcessor, MemoryStateService)
 * - Uses infrastructure services (EventBroadcaster)
 */

import { commandExecutor } from '../commands/executor'
import { eventProcessor, type EventPayload, type ProcessEventResult } from '../events/processor'
import { memoryStateService } from '../state/memory-store'
import { eventBroadcaster } from '../../api/broadcast'
import { logger } from '../utils/logger'
import type { Command, CommandResponse } from '../commands/types'

export class TabSyncCoordinator {
  private broadcastUnsubscribe: (() => void) | null = null
  private snapshotCallback: ((scenarioId: string) => Promise<void>) | null = null
  private remoteUpdateListeners: Set<(scenarioId: string) => void> = new Set()

  /**
   * Initialize the coordinator (sets up cross-tab listeners)
   */
  async initialize(): Promise<void> {
    logger.info('[TabSync] Initializing coordinator...')

    // Listen to scenario updates from other tabs
    this.broadcastUnsubscribe = eventBroadcaster.onScenarioUpdate(async (scenarioId) => {
      logger.info(`[TabSync] 📨 Scenario updated in other tab: ${scenarioId}`)
      
      try {
        // 1. Reload memory from IndexedDB (other tab updated it)
        await memoryStateService.reloadScenario(scenarioId)
        logger.debug(`[TabSync] Reloaded scenario ${scenarioId} from IndexedDB`)
        
        // 2. Notify registered listeners (e.g. ServerSyncService to trigger upload if leader)
        this.remoteUpdateListeners.forEach(listener => listener(scenarioId))
      } catch (error) {
        logger.error('[TabSync] Failed to reload scenario from other tab:', error)
      }
    })

    // Listen for snapshot-required notifications from EventProcessor
    eventProcessor.onEvent((evt) => {
      if ('type' in evt && evt.type === 'snapshot-required') {
        logger.debug(`[TabSync] Handling snapshot-required for scenario ${evt.scenarioId}`)
        if (this.snapshotCallback) {
          this.snapshotCallback(evt.scenarioId).catch((error) => {
            logger.error(`[TabSync] Failed to apply snapshot for scenario ${evt.scenarioId}:`, error)
          })
        }
      }
    })

    logger.info('[TabSync] ✅ Coordinator initialized')
  }

  /**
   * Cleanup (remove listeners)
   */
  cleanup(): void {
    if (this.broadcastUnsubscribe) {
      this.broadcastUnsubscribe()
      this.broadcastUnsubscribe = null
    }
    this.snapshotCallback = null
    logger.debug('[TabSync] Coordinator cleaned up')
  }

  /**
   * Register a callback for snapshot recovery
   * Called by ServerSyncService to handle snapshot-required notifications
   */
  onSnapshotRequired(callback: (scenarioId: string) => Promise<void>): void {
    this.snapshotCallback = callback
  }

  /**
   * Register a callback for remote updates (from other tabs)
   * Called by ServerSyncService to trigger sync when other tabs make changes
   */
  onRemoteUpdate(callback: (scenarioId: string) => void): () => void {
    this.remoteUpdateListeners.add(callback)
    return () => this.remoteUpdateListeners.delete(callback)
  }

  /**
   * Execute a command (called by UI hooks)
   * Flow: Execute locally → Broadcast to other tabs
   */
  async executeCommand(command: Command): Promise<CommandResponse> {
    logger.info(`[TabSync] Executing command: ${command.commandType}`)

    // 1. Execute command through domain service (pure)
    const result = await commandExecutor.executeCommand(command)

    if (result.status === 'success') {
      // 2. Broadcast to other tabs (they'll reload and see pending command in memory)
      logger.debug(`[TabSync] 📢 Broadcasting command to other tabs: ${command.scenarioId}`)
      eventBroadcaster.notifyScenarioUpdate(command.scenarioId)
    } else {
      logger.error(`[TabSync] Command execution failed:`, result.error)
    }

    return result
  }

  /**
   * Process an event from server (called by ServerSyncService, leader tab only)
   * Flow: Process event → Reload memory → Broadcast to other tabs
   */
  async processEvent(eventPayload: EventPayload): Promise<ProcessEventResult> {
    logger.info(`[TabSync] Processing event: ${eventPayload.eventType}`)

    // 1. Process event through domain service (saves to IndexedDB)
    const result = await eventProcessor.processEvent(eventPayload)

    if (result.status === 'success' && result.requiresMemoryReload) {
      // 2. Reload memory from updated IndexedDB (server truth changed)
      logger.debug(`[TabSync] Reloading memory for scenario: ${eventPayload.scenarioId}`)
      try {
        await memoryStateService.reloadScenario(eventPayload.scenarioId)
      } catch (error) {
        logger.error('[TabSync] Failed to reload memory after event:', error)
      }

      // 3. Broadcast to other tabs
      logger.debug(`[TabSync] 📢 Broadcasting event to other tabs: ${eventPayload.scenarioId}`)
      eventBroadcaster.notifyScenarioUpdate(eventPayload.scenarioId)
    }

    return result
  }

  /**
   * Apply a snapshot (called by ServerSyncService)
   * Flow: Apply snapshot → Reload memory → Broadcast to other tabs
   */
  async applySnapshot(scenarioId: string): Promise<void> {
    logger.info(`[TabSync] Applying snapshot for scenario: ${scenarioId}`)

    // Snapshot application is handled by ServerSyncService (loads from API, saves to IndexedDB)
    // We just need to reload memory and broadcast

    // Reload memory from updated IndexedDB
    logger.debug(`[TabSync] Reloading memory for scenario: ${scenarioId}`)
    await memoryStateService.reloadScenario(scenarioId)

    // Broadcast to other tabs
    logger.debug(`[TabSync] 📢 Broadcasting snapshot to other tabs: ${scenarioId}`)
    eventBroadcaster.notifyScenarioUpdate(scenarioId)

    logger.info(`[TabSync] ✅ Snapshot applied for scenario: ${scenarioId}`)
  }

  /**
   * Notify that sync queue changed (update pending count)
   * Called by ServerSyncService after processing commands
   */
  queueChanged(): void {
    // This is a lightweight notification to update UI pending count
    // No need to reload full scenario state
    logger.debug('[TabSync] Queue changed notification')
  }
}

// Singleton instance
export const tabSyncCoordinator = new TabSyncCoordinator()
